import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { ref, watch } from 'vue';

import { Loadable, UserRef } from '@rygen/rygen-ui/model';
import {
  UserIdToUserMap,
  UserStore,
  UserId,
  PerformLoadOptions,
} from '@rygen/rygen-ui/stores/User';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useUserStore = defineStore('user', (): UserStore => {
  const { selectedDomainId } = useCurrentUserStore();

  const usersById: Ref<UserIdToUserMap> = ref({});

  const loadUserById = (
    id: UserId,
    opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
  ) => {
    // TODO: CS-8892 - expiration token
    const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

    if (!usersById.value[id]) {
      usersById.value[id] = { loading: false };
    }

    const userLoadable = usersById.value[id];
    const requiresLoad = !userLoadable.data && !userLoadable.loading;

    return options.bypassCache || requiresLoad
      ? performLoadUserById(id)
      : userLoadable;
  };

  const performLoadUserById = (id: UserId): Loadable<UserRef> => {
    if (!usersById.value[id]) {
      usersById.value[id] = { loading: false };
    }

    const userLoadable = usersById.value[id];
    userLoadable.loading = true;

    api.user
      .findAssignableUser(id)
      .then((userResult) => {
        userLoadable.data = userResult;
        userLoadable.error = undefined;
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          return;
        }

        console.error('Error loading user', e);
        userLoadable.data = undefined;
        userLoadable.error = e;
      })
      .finally(() => {
        userLoadable.loading = false;
      });

    return userLoadable;
  };

  watch(selectedDomainId, () => {
    usersById.value = {};
  });

  return {
    usersById,
    loadUserById,
  };
});

export const useUserStore = (): UserStore => {
  const store = _useUserStore();
  const { usersById } = storeToRefs(store);
  const { loadUserById } = store;

  return {
    usersById,
    loadUserById,
  };
};
